import { useNavigate } from 'react-router-dom';

import { useTranslation } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import useModal from '../../hooks/useModal';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { useAdmins, useUpdateAdmin } from '../../hooks/queries/useAdminQueries';
import { IBackofficeAdmin } from '../../types';
import { Button } from '../ui/button';
import AdminListView from './AdminListView';
import AdminFormDialog from './AdminFormDialog';
import AdminDeleteDialog from './AdminDeleteDialog';
import AdminTransferDialog from './AdminTransferDialog';

const AdminManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const formDialog = useModal<IBackofficeAdmin>();
  const deleteDialog = useModal<IBackofficeAdmin>();
  const transferConfirm = useConfirmDialog();

  const updateAdmin = useUpdateAdmin();
  const { data: admins = [], isLoading } = useAdmins();

  const handleViewDetail = (admin: IBackofficeAdmin) => {
    navigate(`/admin-management/${admin.id}`);
  };

  const handleEditPermissions = (admin: IBackofficeAdmin) => {
    formDialog.open(admin);
  };

  const handleToggleActive = (admin: IBackofficeAdmin) => {
    updateAdmin.mutate({
      id: admin.id,
      data: { isActive: !admin.isActive },
    });
  };

  const handleDelete = (admin: IBackofficeAdmin) => {
    deleteDialog.open(admin);
  };

  return (
    <div className="p-4 lg:p-8 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('admin.title')}
          </h1>
          <p className="text-base text-gray-600 mt-2">{t('admin.subtitle')}</p>
        </div>
        {currentUser?.isOwner && (
          <Button onClick={() => formDialog.open()}>
            {t('admin.create_admin')}
          </Button>
        )}
      </div>

      <AdminListView
        admins={admins}
        isLoading={isLoading}
        currentUserId={currentUser?.id ?? null}
        isOwner={currentUser?.isOwner ?? false}
        onViewDetail={handleViewDetail}
        onEditPermissions={handleEditPermissions}
        onToggleActive={handleToggleActive}
        onDelete={handleDelete}
      />

      <AdminFormDialog
        isOpen={formDialog.isOpen}
        onClose={formDialog.close}
        admin={formDialog.selectedItem}
      />

      <AdminDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        admin={deleteDialog.selectedItem}
      />

      <AdminTransferDialog
        isOpen={transferConfirm.isOpen}
        message={transferConfirm.message}
        isLoading={transferConfirm.isLoading}
        onConfirm={transferConfirm.confirm}
        onCancel={transferConfirm.cancel}
      />
    </div>
  );
};

export default AdminManagement;
